import { teams } from "@/mocks/teams";
import { players } from "@/mocks/players";
import { games } from "@/mocks/games";
import type { Game, GameEvent, GameHighlight, GameTeamStats, Player, Stage, Team } from "@/types";

export const getTeamById = (id: string | undefined): Team | undefined =>
  teams.find((team) => team.id === id);

export const getPlayerById = (id: string | undefined): Player | undefined =>
  players.find((player) => player.id === id);

export const getTeamPlayers = (teamId: string | undefined): Player[] =>
  players.filter((player) => player.teamId === teamId);

export const getGameById = (id: string | undefined): Game | undefined =>
  games.find((game) => game.id === id);

export const getSeasons = (): string[] => {
  const set = new Set(games.map((game) => game.season));
  return Array.from(set).sort().reverse();
};

export const getGamesBySeason = (season: string | undefined): Game[] =>
  games.filter((game) => (season ? game.season === season : true));

export const getAllGames = (): Game[] => games;

export const realTeams = (): Team[] => teams.filter((team) => team.division !== "全明星");

export const stageLabel = (stage: Stage | string): string => {
  if (stage === "playoff") return "季后赛";
  if (stage === "allstar") return "全明星";
  return "常规赛";
};

export const statusLabel = (status: Game["status"]): string => {
  if (status === "live") return "直播中";
  if (status === "upcoming") return "未开始";
  return "已结束";
};

export const formatDate = (date: string): string => {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[1]}月${parts[2]}日`;
};

export const formatFullDate = (date: string): string => {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[0]}年${parts[1]}月${parts[2]}日`;
};

export const getTeamStats = (game: Game, side: "home" | "away"): GameTeamStats => {
  const provided = side === "home" ? game.homeStats : game.awayStats;
  if (provided) return provided;
  const score = side === "home" ? game.homeScore : game.awayScore;
  const base = score > 0 ? score : 100;
  return {
    rebounds: 36 + (base % 12),
    assists: 20 + (base % 10),
    steals: 5 + (base % 6),
    blocks: 3 + (base % 5),
    turnovers: 8 + (base % 8),
    fg: Math.round((44 + (base % 7)) * 10) / 10,
    three: Math.round((33 + (base % 9)) * 10) / 10,
    ft: Math.round((74 + (base % 12)) * 10) / 10,
  };
};

const fallbackNames = ["核心后卫", "首发前锋", "替补中锋", "轮换前锋", "第六人"];

export const getGameEvents = (game: Game): GameEvent[] => {
  if (game.events && game.events.length > 0) return game.events;
  const homePlayers = getTeamPlayers(game.homeTeamId);
  const awayPlayers = getTeamPlayers(game.awayTeamId);
  const quarters = ["第一节", "第二节", "第三节", "第四节"];
  const templates = [
    { desc: "突破上篮命中", weight: 2 },
    { desc: "外线三分远投入网", weight: 3 },
    { desc: "抢下前场篮板补篮得手", weight: 2 },
    { desc: "造成犯规两罚全中", weight: 2 },
    { desc: "快攻反击单手暴扣", weight: 2 },
  ];
  const result: GameEvent[] = [];
  const total = game.status === "upcoming" ? 0 : 8;
  let home = 0;
  let away = 0;
  for (let i = 0; i < total; i += 1) {
    const isHome = i % 2 === 0;
    const tpl = templates[i % templates.length];
    const roster = isHome ? homePlayers : awayPlayers;
    const player = roster.length > 0 ? roster[i % roster.length].name : fallbackNames[i % fallbackNames.length];
    if (isHome) home += tpl.weight;
    else away += tpl.weight;
    result.push({
      id: `${game.id}-ev-${i}`,
      quarter: quarters[Math.min(Math.floor(i / 2), quarters.length - 1)],
      time: `${(8 - i).toString().padStart(2, "0")}:${(i * 7 % 60).toString().padStart(2, "0")}`,
      teamId: isHome ? game.homeTeamId : game.awayTeamId,
      player,
      description: tpl.desc,
      homeScore: home,
      awayScore: away,
    });
  }
  return result;
};

export const getGameHighlights = (game: Game): GameHighlight[] => {
  if (game.highlights && game.highlights.length > 0) return game.highlights;
  const home = getTeamById(game.homeTeamId);
  const away = getTeamById(game.awayTeamId);
  if (!home || !away) return [];
  return [
    { time: "第一节 06:30", label: "开局阶段", description: `${home.fullName}率先进入状态，打出一波小高潮。` },
    { time: "第二节 03:15", label: "半场战报", description: `${away.fullName}顽强追赶，半场比分紧咬。` },
    { time: "第三节 02:40", label: "第三节高潮", description: `双方交替领先，比赛进入白热化阶段。` },
    { time: "第四节 00:30", label: "决胜时刻", description: `${home.fullName}顶住压力，最终以 ${game.homeScore}-${game.awayScore} 结束比赛。` },
  ];
};

export const scoreLabel = (game: Game): string => {
  if (game.status === "upcoming") return "未开始";
  return `${game.homeScore} - ${game.awayScore}`;
};

export const getQuarterScores = (game: Game) => {
  if (game.status === "upcoming") return [];
  const build = (total: number) => {
    const q1 = Math.round(total * 0.26);
    const q2 = Math.round(total * 0.24);
    const q3 = Math.round(total * 0.25);
    const q4 = total - q1 - q2 - q3;
    return [q1, q2, q3, q4];
  };
  const home = build(game.homeScore);
  const away = build(game.awayScore);
  const labels = ["第一节", "第二节", "第三节", "第四节"];
  return labels.map((label, index) => ({ label, home: home[index], away: away[index] }));
};

export const searchAll = (keyword: string) => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return { teams: [], players: [], games: [] };
  const matchedTeams = teams.filter(
    (team) => team.fullName.includes(keyword) || team.abbreviation.toLowerCase().includes(kw) || team.city.includes(keyword),
  );
  const matchedPlayers = players.filter((player) => player.name.includes(keyword));
  const matchedGames = games.filter((game) => {
    const home = getTeamById(game.homeTeamId);
    const away = getTeamById(game.awayTeamId);
    if (!home || !away) return false;
    const label = `${home.fullName}${away.fullName}`;
    return label.includes(keyword) || home.abbreviation.includes(keyword.toUpperCase());
  });
  return { teams: matchedTeams, players: matchedPlayers, games: matchedGames.slice(0, 6) };
};